import { Link } from "react-router-dom";
import { Menu } from "lucide-react";
import { useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { CTAButton } from "./CTAButton";
import { FlagES, FlagUK } from "./Flag";
import { LogoLockup } from "./LogoLockup";
import { GuideDownloadModal } from "./GuideDownloadModal";

export function Header() {
  const { t, path, switchLangPath, otherLang } = useLang();
  const [open, setOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const TargetFlag = otherLang === "es" ? FlagES : FlagUK;
  const targetLabel = otherLang === "es" ? "Cambiar a español" : "Switch to English";

  const openGuide = () => {
    setOpen(false); // close mobile sheet if open
    setGuideOpen(true);
  };

  return (
    <header className="w-full backdrop-blur-xl bg-paper/90 border-b border-borderline">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to={path("home")}
          data-testid="nav-logo-link"
          aria-label="Malinois Writers"
        >
          <LogoLockup variant="horizontal" size="sm" showKicker={false} />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <Link
            to={path("about")}
            data-testid="nav-about-link"
            className="font-mono text-xs uppercase tracking-[0.15em] text-secondary hover:text-ink transition-colors duration-300"
          >
            {t.nav.about}
          </Link>
          <Link
            to={path("services")}
            data-testid="nav-services-link"
            className="font-mono text-xs uppercase tracking-[0.15em] text-secondary hover:text-ink transition-colors duration-300"
          >
            {t.nav.services}
          </Link>
          <Link
            to={path("contact")}
            data-testid="nav-contact-link"
            className="font-mono text-xs uppercase tracking-[0.15em] text-secondary hover:text-ink transition-colors duration-300"
          >
            {t.nav.contact}
          </Link>
          <Link
            to={switchLangPath}
            data-testid="language-toggle-button"
            aria-label={targetLabel}
            className="flex items-center border border-borderline px-2.5 py-1.5 hover:border-accent transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none"
          >
            <TargetFlag className="w-6 h-4" />
          </Link>
          <CTAButton type="button" onClick={openGuide} label={t.nav.guideCta} testId="nav-cta-button" />
        </nav>

        <div className="md:hidden flex items-center gap-4">
          <Link
            to={switchLangPath}
            data-testid="language-toggle-button-mobile"
            aria-label={targetLabel}
            className="flex items-center border border-borderline px-2 py-1"
          >
            <TargetFlag className="w-5 h-3.5" />
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-button" aria-label="Menu" className="text-ink">
                <Menu size={26} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-paper border-borderline text-ink w-[80vw]">
              <div className="flex flex-col gap-8 mt-16">
                <SheetClose asChild>
                  <Link to={path("about")} data-testid="mobile-nav-about-link" className="font-heading italic text-2xl text-ink">
                    {t.nav.about}
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to={path("services")} data-testid="mobile-nav-services-link" className="font-heading italic text-2xl text-ink">
                    {t.nav.services}
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to={path("contact")} data-testid="mobile-nav-contact-link" className="font-heading italic text-2xl text-ink">
                    {t.nav.contact}
                  </Link>
                </SheetClose>
                <CTAButton type="button" onClick={openGuide} label={t.nav.guideCta} testId="mobile-nav-cta-button" wrap />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <GuideDownloadModal open={guideOpen} onOpenChange={setGuideOpen} />
    </header>
  );
}
